/**
 * SupplyPlansLanding.jsx — public /supply-plans landing page.
 *
 * Feb 2026 mid-month PIVOT: no per-visitor LLM cost, no waiting.
 * The founder curates a library of 5 supply plans per grade
 * (Grades 4-8) that regenerates every few months. Every free
 * visitor gets ONE randomly-picked plan for their grade, delivered
 * instantly by email + on-screen.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  Mail,
  Zap,
  Coffee,
  FileText,
  Sparkles,
} from "lucide-react";
import { requestSupplyPlan } from "../lib/supplyPlanFreeApi";

const GRADES = ["Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8"];

const SAMPLE_BLOCKS = [
  {
    subject: "Mathematics",
    minutes: 50,
    lines: [
      "Various types of questions focused on whole numbers and operations.",
    ],
  },
  {
    subject: "Language",
    minutes: 100,
    lines: [
      "Reading passage with questions",
      "Writing task with prompts",
      "Spelling / word-study activities",
    ],
  },
  {
    subject: "Visual Arts",
    minutes: 50,
    lines: ["A guided drawing activity with a reference and space to create."],
  },
  {
    subject: "STEAM Challenge",
    minutes: 50,
    lines: ["A hands-on design task with prompts and group share-out."],
  },
];

const SupplyPlansLanding = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [grade, setGrade] = useState("Grade 5");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await requestSupplyPlan({
        email: email.trim().toLowerCase(),
        grade,
        consentMarketing: consent,
      });
      if (res.token) {
        navigate(`/supply-plans/deliver/${res.token}`);
        return;
      }
      toast.error("Something odd happened. Please try again.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Couldn't send your plan just now. Please try again in a moment.";
      const code = err?.response?.status;
      if (code === 503) {
        toast.error("We're refreshing our library for that grade.", {
          description: "Please try another grade, or start a 7-day free trial for a plan built to your specific day.",
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="supply-plans-landing"
      className="bg-white min-h-[calc(100vh-64px)]"
    >
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#1c2d3d] text-white">
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(1200px_500px_at_-100px_-100px,rgba(150,222,209,0.16),transparent)]"
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-[11px] sm:text-xs font-extrabold uppercase tracking-[0.28em] text-[#96ded1]">
              <Coffee className="inline h-3.5 w-3.5 -mt-0.5 mr-1.5" />
              Emergency supply day plan · Free · Instant · Just your email
            </p>
            <h1
              data-testid="supply-plans-hero-headline"
              className="mt-4 font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            >
              Sick at 6&nbsp;a.m.?
              <br />
              Supply plan in <span className="text-[#96ded1]">one click</span>.
            </h1>
            <p
              data-testid="supply-plans-hero-subhead"
              className="mt-6 text-base sm:text-lg text-white/85 max-w-xl leading-relaxed"
            >
              Pick your grade. We'll email you a full day plan for your supply —
              activities, worksheets, reading passages, and answer keys —
              hand-picked from our teacher library. Ready to print. Zero prep.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/70">
              <span className="inline-flex items-center gap-1.5">
                <Zap className="h-4 w-4 text-[#96ded1]" /> Delivered instantly
              </span>
              <span className="inline-flex items-center gap-1.5">
                <FileText className="h-4 w-4 text-[#96ded1]" /> Worksheets + answer keys included
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Mail className="h-4 w-4 text-[#96ded1]" /> Emailed to you
              </span>
            </div>
          </div>

          {/* Form card */}
          <div className="lg:col-span-5">
            <form
              onSubmit={onSubmit}
              data-testid="supply-plans-form"
              className="bg-white text-[#1c2d3d] rounded-2xl p-6 sm:p-7 shadow-2xl space-y-4"
            >
              <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/80">
                <Sparkles className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                Send me a supply plan
              </p>
              <label className="block">
                <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]">
                  Grade
                </span>
                <select
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  data-testid="supply-plans-grade"
                  className="mt-1.5 w-full rounded-md border border-[#4a6984]/25 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#96ded1] focus:border-[#96ded1]"
                >
                  {GRADES.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]">
                  Your email
                </span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="supply-plans-email"
                  placeholder="you@example.com"
                  className="mt-1.5 w-full rounded-md border border-[#4a6984]/25 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#96ded1] focus:border-[#96ded1] placeholder:text-[#4a6984]/50"
                />
              </label>
              <label className="flex items-start gap-3 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="supply-plans-consent"
                  className="mt-1 h-4 w-4 rounded border-[#4a6984]/30 text-[#1c2d3d] focus:ring-[#96ded1]"
                />
                <span className="text-xs text-[#4a6984] leading-relaxed">
                  Also send me occasional teaching tips and updates from Atomic Edu. (Your supply plan is sent either way.)
                </span>
              </label>
              <button
                type="submit"
                disabled={submitting || !email}
                data-testid="supply-plans-submit"
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-[#1c2d3d] px-5 py-3 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending…" : "Send me a supply plan"}
                {!submitting && <ArrowRight className="h-3.5 w-3.5" />}
              </button>
              <p className="text-[10px] text-[#4a6984]/70 text-center leading-relaxed">
                Want a plan built for <em>your</em> specific day (a topic, your students' reading level, your class names)?{" "}
                <Link to="/pricing" className="font-extrabold text-[#1c2d3d] underline">Start a free trial</Link>.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Sample */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
        <div className="max-w-3xl">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
            Here's what actually shows up
          </p>
          <h2 className="mt-3 font-black text-3xl sm:text-4xl tracking-tight text-[#1c2d3d]">
            A real supply teacher can walk in and run the day.
          </h2>
          <p className="mt-4 text-base text-[#4a6984] leading-relaxed">
            Every supply plan is built for the 250 minutes of instructional time you are responsible for. Actual lessons your class can sink into, and a supply teacher who gets to walk in ready to teach.
          </p>
        </div>
        <div
          data-testid="supply-plans-sample-preview"
          className="mt-8 rounded-2xl border border-[#4a6984]/15 bg-white p-6 sm:p-8 shadow-sm"
        >
          <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
            <FileText className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
            What every plan includes
          </p>
          <ul className="mt-5 divide-y divide-[#4a6984]/10">
            {SAMPLE_BLOCKS.map((block) => (
              <li
                key={block.subject}
                data-testid={`supply-plans-sample-block-${block.subject.toLowerCase().replace(/\s+/g, "-")}`}
                className="py-4 first:pt-0 last:pb-0"
              >
                <div className="flex items-baseline gap-3">
                  <h3 className="font-black text-[15px] sm:text-base text-[#1c2d3d] tracking-tight">
                    {block.subject}
                  </h3>
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
                    {block.minutes} min
                  </span>
                </div>
                <div className="mt-1.5 space-y-1 text-sm leading-relaxed text-[#4a6984]">
                  {block.lines.map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-6 pt-5 border-t border-[#4a6984]/10 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm font-extrabold text-[#1c2d3d]">
              Total: 250 minutes.
            </p>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#2a7d6b]">
              Ready to print · Zero prep
            </p>
          </div>
        </div>
      </section>

      {/* Soft upsell */}
      <section className="bg-[#f4f5f7] border-t border-[#4a6984]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-16 text-center">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
            Beyond the free library
          </p>
          <h2 className="mt-3 font-black text-2xl sm:text-3xl tracking-tight text-[#1c2d3d]">
            Atomic Edu builds these for your whole year.
          </h2>
          <p className="mt-4 text-sm sm:text-base text-[#4a6984] leading-relaxed max-w-2xl mx-auto">
            Unlimited supply plans built for <em>your</em> specific day, full lesson plans, worksheets with answer keys, differentiation for IEPs and ELLs, and report-card language grounded in what you've actually taught. Seven-day free trial, no credit card up front.
          </p>
          <Link
            to="/pricing"
            data-testid="supply-plans-soft-upsell"
            className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030]"
          >
            See what's included
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SupplyPlansLanding;
